/**
 * Business Constants — SSOT compartida entre backend y todos los fronts
 *
 * Enumeraciones del dominio de negocio que DEBEN usarse en vez de strings raw.
 * Cada constante corresponde a un CHECK constraint en schema.sql o a un valor
 * semántico del dominio.
 *
 * Compatible: ES Modules (fronts via Vite) + CommonJS (backend via require)
 *
 * Uso:
 *   import { TICKET_ESTADO, TIPO_MOVIMIENTO } from '@shared/config/business-constants.js'
 *   const { TICKET_ESTADO } = require('...shared/config/business-constants')
 */

// ============================================================================
// TIPOS DE NODO (metadata del árbol fractal)
// ============================================================================

export const TIPO_NODO = {
    OPERADOR: 'operador',
    REPRESENTANTE: 'representante',
    PUNTO_VENTA: 'punto_venta',
};

export const TIPOS_NODO_VALIDOS = Object.values(TIPO_NODO);

// ============================================================================
// WALLET (Nivel de agrupación de saldo)
// ============================================================================

export const NIVEL_WALLET = {
    PUNTO_VENTA: 'punto_venta',
    REPRESENTANTE: 'representante',
    OPERADOR: 'operador',
};

// ============================================================================
// MOVIMIENTOS DE WALLET (tipo_movimiento en tabla movimientos)
// ============================================================================

export const TIPO_MOVIMIENTO = {
    DEPOSITO: 'deposito',
    RETIRO: 'retiro',
    APUESTA: 'apuesta',
    PREMIO: 'premio',
    BONUS: 'bonus',
    PASARELA: 'pasarela',
    REVERSO_RETIRO: 'reverso_retiro',
};

// ============================================================================
// BEARER TICKETS (estados del ciclo de vida)
// ============================================================================

export const TICKET_ESTADO = {
    ACTIVO: 'activo',
    GANADOR: 'ganador',
    PERDEDOR: 'perdedor',
    COBRADO: 'cobrado',
    EXPIRADO: 'expirado',
    ANULADO: 'anulado',
};

// Estados en los que el ticket ya NO debe dinero a nadie: su ciclo terminó.
// Se declara la lista CERRADA (no la de "tiene dinero vivo") a propósito: un
// estado nuevo que alguien agregue arriba queda fuera de esta lista por omisión
// y por lo tanto cuenta como dinero vivo — falla hacia el lado seguro. Al revés
// (enumerar los vivos) un estado nuevo pasaría desapercibido, que es justo el
// bug que dejó borrar un PV con 30 premios sin cobrar (#590).
// Consumidor: los guards de borrado de nodo/sorteo en scripts/mcp/admin-direct.
export const TICKET_ESTADOS_CERRADOS = [
    TICKET_ESTADO.PERDEDOR, // no paga nada
    TICKET_ESTADO.COBRADO, // ya se pagó
    TICKET_ESTADO.EXPIRADO, // venció el plazo de cobro
    TICKET_ESTADO.ANULADO, // se dio de baja
];

// ============================================================================
// ADELANTOS
// ============================================================================

export const ADELANTO_ESTADO = {
    PENDIENTE_CONFORMIDAD: 'pendiente_conformidad',
    ACTIVO: 'activo',
    RECHAZADO: 'rechazado',
    VENCIDO: 'vencido',
    DEVUELTO: 'devuelto',
    COMPENSADO: 'compensado',
};

// ============================================================================
// LIQUIDACIONES
// ============================================================================

export const LIQUIDACION_ESTADO = {
    PENDIENTE: 'pendiente',
    PARCIAL: 'parcial',
    PAGADA: 'pagada',
    COMPENSADA: 'compensada',
};

// ============================================================================
// ROLES DE ADMIN
// ============================================================================

export const ROL_ADMIN = {
    SUPERADMIN: 'superadmin',
    OPERADOR: 'operador',
    REPRESENTANTE: 'representante',
    PUNTO_VENTA: 'punto_venta',
    CAJERO: 'cajero',
};

// ============================================================================
// TIPOS DE DISPOSITIVO
// ============================================================================

export const TIPO_DISPOSITIVO = {
    SCREEN: 'screen',
    VLT: 'vlt',
    CAJA: 'caja',
};

/**
 * Cuántas ruedas puede apilar una TV a la vez.
 *
 * No es una capacidad del aparato sino del diseño: con más de tres, cada rueda
 * queda demasiado baja para leerse desde el mostrador. El techo REAL de un punto
 * de venta es el menor entre este número y cuántos sorteos tiene autorizados —
 * nadie puede mostrar lo que no le autorizaron.
 *
 * Espejo del `MAXIMO_DE_RUEDAS` de la TV nativa
 * (`PreferenciaDeRuedasVisibles.java`), que recorta al dibujar. El backend valida
 * con este mismo número para que asignar de más falle en el momento de guardar,
 * en vez de aceptarse y descartarse en silencio en la pantalla.
 */
export const MAXIMO_DE_RUEDAS_POR_PANTALLA = 3;

// ============================================================================
// ESTADO DE NODO (ciclo de vida)
// ============================================================================

export const NODO_ESTADO = {
    ACTIVO: 'activo',
    INACTIVO: 'inactivo',
    ONBOARDING: 'onboarding',
    SUSPENDIDO: 'suspendido',
    LIQUIDADO: 'liquidado',
};

// ============================================================================
// CJS COMPATIBILITY (backend require)
// ============================================================================

// ============================================================================
// CÓDIGOS HUMANOS (cupones, promos, referidos)
// ============================================================================
// Alfanumérico + guión bajo + medio. Sin espacios, ni unicode invisible
// (zero-width, RTL marks, NBSP), ni símbolos: lookups posteriores usan exact
// match. Si entra "ABC​123" con ZWSP, el SELECT WHERE codigo=? no matchea
// nunca. Aplicar tras `trimCamposTextoDePayload` en el boundary.
export const CODIGO_HUMANO_REGEX = /^[A-Za-z0-9_-]+$/;
export const CODIGO_HUMANO_MAX_LEN = 64;

if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
        TIPO_NODO,
        TIPOS_NODO_VALIDOS,
        NIVEL_WALLET,
        TIPO_MOVIMIENTO,
        TICKET_ESTADO,
        ADELANTO_ESTADO,
        LIQUIDACION_ESTADO,
        ROL_ADMIN,
        TIPO_DISPOSITIVO,
        MAXIMO_DE_RUEDAS_POR_PANTALLA,
        NODO_ESTADO,
        CODIGO_HUMANO_REGEX,
        CODIGO_HUMANO_MAX_LEN,
    };
}
